import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { client } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ScriptureCard from "@/components/ScriptureCard";
import { BreakChainButton } from "@/components/BreakChain";
import CelebrationModal from "@/components/CelebrationModal";
import StreakRing from "@/components/StreakRing";
import { SUBSTANCES, MILESTONES } from "@/data/content";
import {
  Coins, CalendarClock, HeartPulse, ArrowRight, Clock, Plus, ShieldCheck, Sparkles, Compass,
  Cigarette, Cloud, Package, Award, Share2, BookOpenText, HandHeart, HeartHandshake, X,
} from "lucide-react";

const AVOIDED_NOUN = { cigarettes: "cigarettes not smoked", puffs: "puffs avoided", pouches: "pouches skipped" };
const SUB_ICON = { cigarettes: Cigarette, vaping: Cloud, chew: Package };

const Glance = ({ icon: Icon, value, label, testid }) => (
  <div className="flex items-center gap-3.5 rounded-2xl bg-card border border-border px-5 py-4" data-testid={testid}>
    <div className="rounded-xl bg-sage w-10 h-10 flex items-center justify-center shrink-0">
      <Icon className="w-[18px] h-[18px] text-brand-teal-dark" />
    </div>
    <div className="min-w-0">
      <p className="font-serif text-2xl text-olive leading-none">{value}</p>
      <p className="text-xs text-stone mt-1 leading-tight">{label}</p>
    </div>
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState([]);
  const [myPrayer, setMyPrayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [celebration, setCelebration] = useState(null);
  const celebrateFired = useRef(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const prayerOnce = useRef(false);

  useEffect(() => {
    Promise.all([client.get("/dashboard"), client.get("/taper/overview")])
      .then(([d, o]) => { setData(d.data); setOverview(o.data.plans || []); })
      .finally(() => setLoading(false));
    if (prayerOnce.current) return;
    prayerOnce.current = true;
    client.get("/prayer/mine").then((r) => {
      setMyPrayer(r.data.request);
      if (r.data.request?.new_prayers > 0) client.post("/prayer/ack").catch(() => {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const ap = data?.active_plan;
    if (!ap || celebrateFired.current) return;
    const ms = MILESTONES[ap.substance] || [];
    const celebrated = ap.celebrated || [];
    const reached = ms.filter((m) => (data.hours_since_quit || 0) >= m.hours);
    const unceleb = reached.filter((m) => !celebrated.includes(m.title));
    if (unceleb.length) {
      celebrateFired.current = true;
      setCelebration({ milestone: unceleb[unceleb.length - 1] });
      client.post(`/plans/${ap.id}/celebrate`, { keys: reached.map((m) => m.title) }).catch(() => {});
    }
  }, [data]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  if (loading) {
    return <div className="animate-pulse text-stone" data-testid="dashboard-loading">Gathering your journey…</div>;
  }

  const active = data?.active_plan;
  const sub = active ? SUBSTANCES[active.substance] : null;
  const currencySymbol = data?.currency === "USD" ? "$" : "";
  const evidence = data?.evidence || [];
  const milestones = active ? (MILESTONES[active.substance] || []) : [];
  const nextMilestone = milestones.find((m) => data.hours_since_quit < m.hours);
  const daysToQuit = active ? Math.max(0, Math.ceil((new Date(active.quit_date) - new Date()) / 86400000)) : 0;
  const avoidedNoun = AVOIDED_NOUN[data?.avoided_unit] || "avoided so far";

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="uppercase tracking-[0.2em] text-xs text-brand-teal-dark mb-2">{greeting()}</p>
        <h1 className="font-serif text-display text-olive">
          Peace be with you, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="text-olive/70 mt-3">Here's what matters right now — one moment at a time.</p>
        {active && (active.attempts || 1) > 1 && (
          <span data-testid="today-attempt-badge" className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-brand-teal/15 text-brand-teal-dark px-3 py-1.5 text-xs font-semibold">
            <Award className="w-3.5 h-3.5" /> Attempt #{active.attempts} · begun again {active.attempts - 1}× — that's perseverance
          </span>
        )}
      </motion.div>

      {myPrayer?.new_prayers > 0 && !nudgeDismissed && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          data-testid="prayer-nudge"
          className="relative rounded-3xl bg-brand-navy text-brand-ivory p-6 shadow-soft">
          <button data-testid="prayer-nudge-dismiss" onClick={() => setNudgeDismissed(true)}
            className="absolute top-4 right-4 text-brand-ivory/60 hover:text-brand-ivory transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <HeartHandshake className="w-4 h-4 text-brand-teal" />
            <p className="text-[11px] uppercase tracking-[0.15em] text-gold">Someone prayed with you</p>
          </div>
          <p className="font-serif text-xl leading-relaxed" data-testid="prayer-nudge-text">
            {myPrayer.new_prayers === 1
              ? "Since you were last here, someone stopped to pray with you."
              : `Since you were last here, ${myPrayer.new_prayers} people stopped to pray with you.`}
          </p>
          <p className="text-brand-ivory-muted text-sm mt-2">You're not carrying this alone. "Carry each other's burdens." (Galatians 6:2)</p>
          <Link to="/app/together" data-testid="prayer-nudge-link" className="inline-flex items-center gap-1.5 text-sm text-brand-teal hover:underline mt-3">
            See the Prayer Wall <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      )}

      {!active ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-card border border-border p-8 sm:p-10 shadow-soft text-center" data-testid="no-plan-cta">
          <div className="rounded-2xl bg-sage w-14 h-14 flex items-center justify-center mx-auto mb-5">
            <Compass className="w-6 h-6 text-brand-teal-dark" />
          </div>
          <h2 className="font-serif text-3xl text-olive mb-2">Your journey begins here</h2>
          <p className="text-olive/70 max-w-md mx-auto mb-6">
            Set your intentions, name your reasons, and build a gentle plan for the road to freedom — not by willpower alone, but by grace.
          </p>
          <Link to="/app/journey" data-testid="start-journey-link" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sand font-semibold transition-colors duration-200 hover:bg-terracotta-soft">
            Start my journey <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      ) : (
        <>
          {/* Break the Chain — the signature action */}
          <BreakChainButton variant="banner" />

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Where you are + next action */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="today-focus-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-2xl text-olive">Where you are today</h2>
                  <Link to="/app/plan" className="text-sm text-terracotta flex items-center gap-1 hover:underline">
                    Full plan <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                <div className="space-y-3" data-testid="today-substances">
                  {overview.map((p) => {
                    const I = SUB_ICON[p.substance] || Clock;
                    const unit = p.unit === "mg" ? " mg" : "";
                    return (
                      <Link key={p.id} to="/app/tracker" data-testid={`today-substance-${p.substance}`}
                        className="flex items-start gap-4 rounded-2xl bg-sage p-5 transition-colors hover:bg-sage/70">
                        <div className="rounded-2xl bg-sand p-3 shrink-0"><I className="w-6 h-6 text-brand-teal-dark" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-medium text-olive capitalize">{p.substance}</p>
                            <span className={`text-sm shrink-0 ${p.today.over ? "text-terracotta font-medium" : "text-stone"}`}>
                              {p.today.count}{unit} / {p.today.target}{unit} today
                            </span>
                          </div>
                          {p.step ? (
                            <p className="text-olive/70 text-sm mt-0.5">{p.step.label}{p.step.note ? ` — ${p.step.note}` : ""}</p>
                          ) : (
                            <p className="text-olive/70 text-sm mt-0.5">Getting ready — your plan begins {p.start_date ? `on ${new Date(p.start_date).toLocaleDateString()}` : "soon"}.</p>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                  {overview.length === 0 && <p className="text-olive/70">Your plan is being set up.</p>}
                </div>

                {/* Single next action */}
                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-2">Your next step</p>
                  <Link to="/app/tracker" data-testid="today-next-action"
                    className="flex items-center justify-between rounded-2xl border border-border p-4 transition-colors hover:bg-sage">
                    <span className="flex items-center gap-3 text-olive font-medium"><HeartPulse className="w-4 h-4 text-brand-teal-dark" /> Log today in Progress</span>
                    <ArrowRight className="w-4 h-4 text-stone" />
                  </Link>
                </div>
              </div>

              {/* Progress glance */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-stone">Evidence of change</p>
                  <div className="flex items-center gap-3">
                    {data.days_clean > 0 && (
                      <button data-testid="share-progress-btn" onClick={() => setCelebration({ milestone: null })} className="text-xs text-brand-teal-dark hover:underline flex items-center gap-1">
                        <Share2 className="w-3 h-3" /> Share
                      </button>
                    )}
                    <Link to="/app/tracker" className="text-xs text-terracotta hover:underline flex items-center gap-1">See progress <ArrowRight className="w-3 h-3" /></Link>
                  </div>
                </div>
                {evidence.length > 1 ? (
                  <div className="space-y-3">
                    <Glance testid="glance-money" icon={Coins} value={`${currencySymbol}${data.money_saved}`} label="Money saved across all plans" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {evidence.map((e) => {
                        const unit = e.unit === "mg" ? " mg" : "";
                        const noun = AVOIDED_NOUN[e.unit] || "avoided";
                        const dToQuit = e.quit_date ? Math.max(0, Math.ceil((new Date(e.quit_date) - new Date()) / 86400000)) : 0;
                        return (
                          <div key={e.substance} data-testid={`evidence-${e.substance}`} className="rounded-2xl bg-card border border-border px-5 py-4">
                            <p className="font-medium text-olive capitalize mb-1">{e.substance}</p>
                            <p className="font-serif text-2xl text-olive leading-none">{e.units_avoided}{unit}</p>
                            <p className="text-xs text-stone mt-1">{noun}</p>
                            <p className="text-xs text-brand-teal-dark mt-2">{e.days_clean > 0 ? `${e.days_clean} days reclaimed` : `${dToQuit} days to quit day`}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Glance testid="glance-money" icon={Coins} value={`${currencySymbol}${data.money_saved}`} label="Money saved" />
                    <Glance testid="glance-days" icon={CalendarClock}
                      value={data.days_clean > 0 ? data.days_clean : daysToQuit}
                      label={data.days_clean > 0 ? "Days reclaimed" : "Days to quit day"} />
                    <Glance testid="glance-avoided" icon={ShieldCheck} value={data.units_avoided ?? 0} label={avoidedNoun} />
                  </div>
                )}
              </div>
            </div>

            {/* Scripture + a word of hope */}
            <div className="space-y-6">
              {data.days_clean > 0 && <StreakRing days={data.days_clean} />}
              <ScriptureCard compact />
              <Link to="/app/verses" data-testid="browse-verses-link" className="flex items-center justify-between rounded-2xl border border-border p-4 transition-colors hover:bg-sage">
                <span className="flex items-center gap-2.5 text-olive font-medium"><BookOpenText className="w-4 h-4 text-terracotta" /> Browse verses by theme</span>
                <ArrowRight className="w-4 h-4 text-stone" />
              </Link>

              {/* Prayer Wall — stand with others / be stood with */}
              <div data-testid="today-prayer-card"
                className="rounded-3xl bg-brand-navy text-brand-ivory p-6 shadow-soft">
                <div className="flex items-center gap-2 mb-2">
                  <HandHeart className="w-4 h-4 text-brand-teal" />
                  <p className="text-[11px] uppercase tracking-[0.15em] text-gold">Together</p>
                </div>
                {myPrayer ? (
                  <p className="font-serif text-xl leading-relaxed" data-testid="today-prayer-mine">
                    {myPrayer.pray_count === 0
                      ? "Your prayer request is on the wall. You're not alone."
                      : `${myPrayer.pray_count} ${myPrayer.pray_count === 1 ? "person is" : "people are"} praying with you.`}
                  </p>
                ) : (
                  <p className="font-serif text-xl leading-relaxed">Carrying something heavy, or want to lift someone up? You're not alone here.</p>
                )}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                  <Link to="/app/together" data-testid="today-goto-wall" className="inline-flex items-center gap-1.5 text-sm text-brand-teal hover:underline">
                    Go to the Prayer Wall <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  {!myPrayer && (
                    <Link to="/app/together?ask=1" data-testid="today-ask-prayer" className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline">
                      <HandHeart className="w-3.5 h-3.5" /> Ask for prayer
                    </Link>
                  )}
                </div>
              </div>
              {nextMilestone && (
                <div className="rounded-3xl bg-sage p-7" data-testid="next-milestone">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-gold" />
                    <p className="text-[11px] uppercase tracking-[0.15em] text-stone">Coming up · {nextMilestone.title}</p>
                  </div>
                  <p className="font-serif text-xl text-olive leading-relaxed">{nextMilestone.desc}</p>
                  <Link to="/app/journey" className="inline-flex items-center gap-1.5 text-sm text-terracotta mt-4 hover:underline">
                    See your whole journey <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <CelebrationModal
        open={!!celebration}
        onClose={() => setCelebration(null)}
        days={data?.days_clean || 0}
        label="days reclaimed"
        milestone={celebration?.milestone?.title || null}
        milestoneDesc={celebration?.milestone?.desc || ""}
        money={data?.money_saved || 0}
        currency={currencySymbol}
      />
    </div>
  );
}
